/**
 * As vantagens da CONTA do banco, pelo programa e pelo NÍVEL em que ela está.
 *
 * Desenho: `docs/designs/vantagens-da-conta.md` (13/10/2026). É o irmão de
 * `vantagensDoCartao.ts`: lá o selo sai da bandeira do cartão; aqui, do
 * programa de relacionamento da conta.
 *
 * ── De onde vem cada linha ───────────────────────────────────────────────
 *
 * Da página oficial do banco, lida por gente e resumida com as nossas
 * palavras, com o link e a data. O nível quem informa é ELA: o Open Finance só
 * diz "conta corrente", e o nível do Minhas Vantagens só aparece no app do
 * banco.
 *
 * Os níveis são CUMULATIVOS: o nível 4 leva tudo dos níveis 1 a 3. `nivel` em
 * cada linha é o primeiro nível em que o benefício aparece.
 *
 * ── O pacote ─────────────────────────────────────────────────────────────
 *
 * O que a conta inclui por mês (saques, transferências) vem dos dados abertos
 * do Open Finance do banco, uma API pública. É uma leitura datada, como o
 * resto: a franquia muda quando o banco muda o pacote.
 *
 * ── O texto informa e manda conferir ─────────────────────────────────────
 *
 * "O que o nível oferece", nunca "você tem direito": voucher e desconto têm
 * regra própria no app do banco, e o banco troca os parceiros sem avisar.
 */

export interface VantagemDaConta {
  id: string;
  /** O primeiro nível em que vale. */
  nivel: number;
  nome: string;
}

export interface ProgramaDaConta {
  id: string;
  /** "Itaú Uniclass": o que aparece no selo. */
  nome: string;
  /** O nome do programa de relacionamento, como o banco chama. */
  programa: string;
  niveis: number[];
  /** A página oficial de onde a lista saiu. */
  fonte: string;
  /** 'AAAA-MM-DD' da última conferência. */
  revisadoEm: string;
  vantagens: VantagemDaConta[];
  /** Só onde a franquia foi lida nos dados abertos do banco. */
  pacote?: {
    nome: string;
    /** O que a conta inclui por mês, segundo os dados abertos do banco. */
    itens: string[];
    nota: string;
    fonte: string;
  };
}

/** Programa sem níveis (um segmento): quem é cliente tem tudo. */
export const semNiveis = (p: Pick<ProgramaDaConta, 'niveis'>): boolean => p.niveis.length === 1;

export const MESES_ATE_REVISAR = 6;

const UNICLASS = 'https://www.itau.com.br/uniclass/minhas-vantagens';
const HOJE = '2026-10-13';

export const PROGRAMAS_DA_CONTA: ProgramaDaConta[] = [
  {
    id: 'itau-uniclass',
    nome: 'Itaú Uniclass',
    programa: 'Minhas Vantagens',
    niveis: [1, 2, 3, 4, 5],
    fonte: UNICLASS,
    revisadoEm: HOJE,
    vantagens: [
    { id: 'uniclass-gerente', nivel: 1, nome: 'Gerente dedicado e especialistas' },
    { id: 'uniclass-signature', nivel: 1, nome: 'Cartão Uniclass Signature com tarifa zero' },
    { id: 'uniclass-lojas', nivel: 1, nome: 'Desconto e cashback em lojas parceiras' },
    { id: 'uniclass-dez-dias', nivel: 2, nome: '10 dias sem juros no limite da conta' },
    { id: 'uniclass-bike', nivel: 2, nome: 'Bike Itaú: 2 viagens gratuitas no mês' },
    { id: 'uniclass-vidaclass', nivel: 2, nome: 'VidaClass: acesso gratuito para cuidar da saúde' },
    { id: 'uniclass-tags', nivel: 2, nome: 'Até 2 Tags Itaú livres de mensalidade' },
    { id: 'uniclass-carro', nivel: 2, nome: 'Desconto em aluguel de carros' },
    { id: 'uniclass-vinhos', nivel: 2, nome: 'Voucher de desconto em vinhos na Evino' },
    { id: 'uniclass-anuidade-platinum', nivel: 3, nome: 'Anuidade gratuita nos cartões Signature e Platinum' },
    { id: 'uniclass-smartfit', nivel: 3, nome: 'Desconto na mensalidade da Smart Fit' },
    { id: 'uniclass-imobiliario', nivel: 3, nome: 'Crédito imobiliário com taxas mais baixas' },
    { id: 'uniclass-tarifa', nivel: 3, nome: 'Isenção da tarifa da conta corrente' },
    { id: 'uniclass-spa', nivel: 3, nome: 'Desconto no Buddha Spa' },
    { id: 'uniclass-pontos', nivel: 3, nome: 'Até 2 pontos por dólar no cartão (consulte as regras)' },
    { id: 'uniclass-voucher', nivel: 4, nome: 'Voucher de R$ 20 na Uber ou em app de delivery todo mês' },
    { id: 'uniclass-anuidade-black', nivel: 4, nome: 'Anuidade gratuita nos cartões Black ou Infinite' },
    { id: 'uniclass-streaming', nivel: 5, nome: 'Voucher em plataforma de filmes e séries' },
    { id: 'uniclass-mil-pontos', nivel: 5, nome: '1.000 pontos Itaú para resgatar' },
    { id: 'uniclass-sala-vip', nivel: 5, nome: '4 acessos gratuitos a salas VIP (consulte as regras)' },
    ],
    pacote: {
      nome: 'Conta Itaú Uniclass (pacote 4.0)',
      itens: [
        'Saques ilimitados',
        'Transferências entre contas Itaú ilimitadas',
        '6 TEDs ou DOCs por mês pelos canais eletrônicos, e 1 no atendimento pessoal',
        '40 folhas de cheque por mês',
        'Extratos nos canais eletrônicos e avisos por SMS sem limite',
      ],
      nota: 'O banco publica dois pacotes Uniclass (3.0 e 4.0); este é o mais completo. '
        + 'A tarifa do pacote é isenta a partir do nível 3.',
      fonte: 'https://api.itau/open-banking/opendata-accounts/v1/personal-accounts',
    },
  },
  {
    id: 'itau-personnalite',
    nome: 'Itaú Personnalité',
    programa: 'Minhas Vantagens',
    niveis: [1, 2, 3, 4, 5, 6],
    fonte: 'https://www.itau.com.br/personnalite/minhas-vantagens',
    revisadoEm: HOJE,
    vantagens: [
    { id: 'personnalite-corretagem', nivel: 1, nome: 'Isenção na taxa de corretagem para investir' },
    { id: 'personnalite-tags', nivel: 1, nome: 'Tags Itaú sem mensalidade' },
    { id: 'personnalite-dez-dias', nivel: 1, nome: '10 dias sem juros no limite da conta' },
    { id: 'personnalite-valet', nivel: 1, nome: 'Valet Itaú Personnalité no aeroporto de Guarulhos' },
    { id: 'personnalite-menu', nivel: 1, nome: 'Experiência e Menu Personnalité' },
    { id: 'personnalite-fasano', nivel: 1, nome: 'Benefícios na rede Fasano' },
    { id: 'personnalite-essential', nivel: 2, nome: 'Cartão The Essential sem anuidade, com 2 acessos a salas VIP LoungeKey' },
    { id: 'personnalite-black', nivel: 2, nome: 'Cartões Mastercard Black e Visa Infinite sem anuidade' },
    { id: 'personnalite-tarifa', nivel: 2, nome: 'Conta corrente com isenção de tarifa' },
    { id: 'personnalite-pontuacao', nivel: 2, nome: 'Pontuação do cartão que sobe a cada nível (consulte as regras)' },
    { id: 'personnalite-seis-acessos', nivel: 3, nome: '6 acessos a salas VIP LoungeKey com o The Essential' },
    { id: 'personnalite-mimos', nivel: 3, nome: 'Ingresso de cinema, Uber, Pontos Itaú e outros' },
    { id: 'personnalite-frete', nivel: 3, nome: 'Frete grátis no Itaú Shop' },
    { id: 'personnalite-credito', nivel: 3, nome: 'Desconto na taxa de crédito para investidores' },
    { id: 'personnalite-oito-acessos', nivel: 4, nome: '8 acessos a salas VIP LoungeKey com o The Essential' },
    { id: 'personnalite-vinho', nivel: 4, nome: 'Vinho, além de cinema, Uber e Pontos Itaú' },
    { id: 'personnalite-imobiliario', nivel: 4, nome: 'Taxas diferenciadas de crédito imobiliário' },
    { id: 'personnalite-the-one', nivel: 5, nome: 'Cartão The One sem anuidade, com acesso ilimitado a salas VIP LoungeKey' },
    { id: 'personnalite-cambio', nivel: 5, nome: 'Taxa de câmbio diferenciada em dólar e euro' },
    { id: 'personnalite-legend', nivel: 6, nome: 'Cartão The World Legend sem anuidade' },
    { id: 'personnalite-pedra-preta', nivel: 6, nome: 'Acesso ilimitado do titular à Sala Pedra Preta' },
    { id: 'personnalite-sucessorio', nivel: 6, nome: 'Planejamento sucessório' },
    { id: 'personnalite-exterior', nivel: 6, nome: 'Assessoria para investir no exterior' },
    ],
  },
  /*
   * Os dois abaixo são SEGMENTOS, sem níveis: quem é cliente tem o pacote
   * inteiro. `niveis: [1]` é como a tabela diz "nível único"; a tela não
   * pergunta o nível e o selo não o mostra.
   */
  {
    id: 'santander-select',
    nome: 'Santander Select',
    programa: 'Santander Select',
    niveis: [1],
    fonte: 'https://www.santander.com.br/select/conta',
    revisadoEm: HOJE,
    vantagens: [
    { id: 'select-bilingue', nivel: 1, nome: 'Atendimento bilíngue 24 horas por telefone' },
    { id: 'select-work-cafe', nivel: 1, nome: 'Espaços Work/Café para trabalhar e fazer reuniões' },
    { id: 'select-global', nivel: 1, nome: 'Conta Select Global, com saques sem taxa no exterior' },
    { id: 'select-eua', nivel: 1, nome: 'Atendimento especializado para investir nos Estados Unidos' },
    { id: 'select-rewards', nivel: 1, nome: 'Santander Rewards: mais pontos no cartão conforme o relacionamento' },
    ],
  },
  {
    id: 'nubank-ultravioleta',
    nome: 'Nubank Ultravioleta',
    programa: 'Nubank Ultravioleta',
    niveis: [1],
    fonte: 'https://nubank.com.br/ultravioleta',
    revisadoEm: HOJE,
    vantagens: [
    { id: 'uv-atendimento', nivel: 1, nome: 'Atendimento exclusivo 24 horas' },
    { id: 'uv-cashback', nivel: 1, nome: 'Cartão com cashback de 1,25% ou pontos a partir de 2,2 por dólar' },
    { id: 'uv-salas', nivel: 1, nome: '4 visitas por ano a salas VIP Priority Pass e Lounge Ultravioleta em Guarulhos' },
    { id: 'uv-global', nivel: 1, nome: 'Conta Global com spread zero' },
    { id: 'uv-seguro', nivel: 1, nome: 'Seguro viagem para você e sua família' },
    { id: 'uv-esim', nivel: 1, nome: 'eSIM internacional com 10 GB' },
    { id: 'uv-mensalidade', nivel: 1, nome: 'Mensalidade zerada com R$ 8 mil de gastos no cartão ou R$ 50 mil guardados' },
    ],
  },
];

export const programaDaConta = (id?: string | null): ProgramaDaConta | null =>
  PROGRAMAS_DA_CONTA.find(p => p.id === id) ?? null;

/** As vantagens de quem está naquele nível: as dele e as de todos os anteriores. */
export const vantagensDaConta = (programaId?: string | null, nivel?: number | string | null): VantagemDaConta[] => {
  const p = programaDaConta(programaId);
  const n = Number(nivel);
  if (!p || !Number.isFinite(n) || n < 1) return [];
  return p.vantagens.filter(v => v.nivel <= n);
};

/** "Itaú Uniclass · nível 4", para o selo. Vazio quando não há o que mostrar. */
export const nomeDaConta = (programaId?: string | null, nivel?: number | string | null): string => {
  const p = programaDaConta(programaId);
  if (!p) return '';
  const n = Number(nivel);
  if (!Number.isFinite(n) || n < 1) return '';
  return semNiveis(p) ? p.nome : `${p.nome} · nível ${n}`;
};
